/**
 * WebSocketMonitor - Monitor and manage WebSocket connections
 * Real-time connection status, message statistics, and connection management
 */

'use client';

import React, { useState, useEffect } from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography'
import { Card, Table, Tag, Space, Button, Statistic, Row, Col, Badge, Tooltip, Modal, Alert, Progress, Switch, Timeline, } from 'antd';
import {
  WifiOutlined,
  DisconnectOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  EyeOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { formatNumber, formatDate } from '@/shared/utils/formatters';
import { useWebSocketManager } from '@/infrastructure/websocket/useWebSocketManager';

const { Title, Text } = Typography;

interface WebSocketMonitorProps {
  /** Whether to show detailed connection info */
  detailed?: boolean;
  /** Auto-connect to default endpoints */
  autoConnect?: boolean;
}

export const WebSocketMonitor: React.FC<WebSocketMonitorProps> = ({
  detailed = true,
  autoConnect = true,
}) => {
  const {
    connections,
    connect,
    disconnect,
    disconnectAll,
    sendMessage,
    subscribe,
    unsubscribe,
    getConnectionStatus,
    isConnected,
    getLatency,
    reconnect,
    reconnectAll,
  } = useWebSocketManager();

  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [autoReconnect, setAutoReconnect] = useState(true);
  const [messageLog, setMessageLog] = useState<Array<{
    id: string;
    connectionId: string;
    type: 'sent' | 'received';
    data: any;
    timestamp: Date;
  }>>([]);

  // Auto-connect to default WebSocket endpoints
  useEffect(() => {
    if (autoConnect) {
      // Ticket-based channels: remint on every connect/reconnect (SOC2)
      const defaultConnections = [
        {
          id: 'market-data',
          config: {
            channel: 'market-data' as const,
            useTicket: true,
            requireTicket: false,
            softFailAuth: true,
            onMessage: (data: any) => {
              setMessageLog(prev => [...prev.slice(-99), {
                id: Date.now().toString(),
                connectionId: 'market-data',
                type: 'received',
                data,
                timestamp: new Date(),
              }]);
            },
            onOpen: () => {
              // Subscribe to default channels
              subscribe('market-data', 'quotes');
              subscribe('market-data', 'trades');
            },
          },
        },
        {
          id: 'order-updates',
          config: {
            channel: 'orders' as const,
            useTicket: true,
            requireTicket: true,
            softFailAuth: false,
            onMessage: (data: any) => {
              setMessageLog(prev => [...prev.slice(-99), {
                id: Date.now().toString(),
                connectionId: 'order-updates',
                type: 'received',
                data,
                timestamp: new Date(),
              }]);
            },
            onOpen: () => {
              subscribe('order-updates', 'executions');
              subscribe('order-updates', 'fills');
            },
          },
        },
        {
          id: 'system-notifications',
          config: {
            channel: 'notifications' as const,
            useTicket: true,
            requireTicket: true,
            softFailAuth: false,
            onMessage: (data: any) => {
              setMessageLog(prev => [...prev.slice(-99), {
                id: Date.now().toString(),
                connectionId: 'system-notifications',
                type: 'received',
                data,
                timestamp: new Date(),
              }]);
            },
            onOpen: () => {
              subscribe('system-notifications', 'alerts');
              subscribe('system-notifications', 'system');
            },
          },
        },
      ];

      defaultConnections.forEach(({ id, config }) => {
        connect(id, config);
      });
    }
  }, [autoConnect, connect, subscribe]);

  const getStatusColor = (status: string) => {
    const colors = {
      connected: '#52c41a',
      connecting: '#1890ff',
      disconnected: '#d9d9d9',
      reconnecting: '#faad14',
      error: '#ff4d4f',
    };
    return colors[status as keyof typeof colors] || '#d9d9d9';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      connected: <CheckCircleOutlined />,
      connecting: <SyncOutlined spin />,
      disconnected: <DisconnectOutlined />,
      reconnecting: <SyncOutlined spin />,
      error: <ExclamationCircleOutlined />,
    };
    return icons[status as keyof typeof icons] || <DisconnectOutlined />;
  };

  const handleConnectionAction = (connectionId: string, action: 'connect' | 'disconnect' | 'reconnect') => {
    switch (action) {
      case 'connect':
        // Would need to implement connection form
        break;
      case 'disconnect':
        disconnect(connectionId);
        break;
      case 'reconnect':
        reconnect(connectionId);
        break;
    }
  };

  const handleViewDetails = (connectionId: string) => {
    setSelectedConnection(connectionId);
    setDetailsVisible(true);
  };

  const sendTestMessage = (connectionId: string) => {
    const testMessage = {
      type: 'test',
      timestamp: Date.now(),
      message: 'Test message from WebSocket monitor',
    };

    const success = sendMessage(connectionId, testMessage);
    if (success) {
      setMessageLog(prev => [...prev.slice(-99), {
        id: Date.now().toString(),
        connectionId,
        type: 'sent',
        data: testMessage,
        timestamp: new Date(),
      }]);
    }
  };

  const connectionArray = Object.values(connections);
  const connectedCount = connectionArray.filter(c => c.state.status === 'connected').length;
  const totalMessages = connectionArray.reduce((sum, c) => sum + c.state.messageCount, 0);
  const totalErrors = connectionArray.reduce((sum, c) => sum + c.state.errorCount, 0);
  const avgLatency = connectionArray.length > 0
    ? connectionArray.reduce((sum, c) => sum + (c.state.latency || 0), 0) / connectionArray.length
    : 0;

  const columns = [
    {
      title: 'Connection',
      key: 'connection',
      render: (_: any, record: any) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.id}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.config.url}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => (
        <Space orientation="vertical" size={0}>
          <Tag
            color={getStatusColor(record.state.status)}
            icon={getStatusIcon(record.state.status)}
          >
            {record.state.status.toUpperCase()}
          </Tag>
          {record.state.lastConnected && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(record.state.lastConnected.toISOString(), { timeStyle: 'short' })}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Messages',
      key: 'messages',
      render: (_: any, record: any) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{formatNumber(record.state.messageCount)}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.state.errorCount} errors
          </Text>
        </Space>
      ),
    },
    {
      title: 'Latency',
      key: 'latency',
      render: (_: any, record: any) => (
        <Text style={{ color: record.state.latency ? (record.state.latency < 100 ? '#52c41a' : record.state.latency < 300 ? '#faad14' : '#ff4d4f') : '#d9d9d9' }}>
          {record.state.latency ? `${record.state.latency}ms` : '-'}
        </Text>
      ),
    },
    {
      title: 'Subscriptions',
      key: 'subscriptions',
      render: (_: any, record: any) => (
        <Badge count={record.subscriptions.size} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {record.state.status === 'connected' ? (
            <Tooltip title="Disconnect">
              <Button
                type="text"
                icon={<DisconnectOutlined />}
                onClick={() => handleConnectionAction(record.id, 'disconnect')}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Reconnect">
              <Button
                type="text"
                icon={<SyncOutlined />}
                onClick={() => handleConnectionAction(record.id, 'reconnect')}
              />
            </Tooltip>
          )}
          <Tooltip title="Send Test Message">
            <Button
              type="text"
              icon={<ThunderboltOutlined />}
              onClick={() => sendTestMessage(record.id)}
              disabled={record.state.status !== 'connected'}
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* WebSocket Overview */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Connected"
              value={connectedCount}
              suffix={`/ ${connectionArray.length}`}
              prefix={<WifiOutlined />}
              styles={{ content: { color: connectedCount > 0 ? '#52c41a' : '#ff4d4f' }} }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Messages"
              value={totalMessages}
              prefix={<BarChartOutlined />}
              styles={{ content: { color: '#1890ff' }} }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Latency"
              value={Math.round(avgLatency)}
              suffix="ms"
              prefix={<ThunderboltOutlined />}
              styles={{ content: { color: avgLatency < 100 ? '#52c41a' : avgLatency < 300 ? '#faad14' : '#ff4d4f' }} }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Errors"
              value={totalErrors}
              prefix={<ExclamationCircleOutlined />}
              styles={{ content: { color: totalErrors > 0 ? '#ff4d4f' : '#52c41a' }} }
            />
          </Card>
        </Col>
      </Row>

      {/* Connection Status Alerts */}
      {totalErrors > 0 && (
        <Alert
          title="WebSocket Connection Issues"
          description={`${totalErrors} connection errors detected. Check individual connection status for details.`}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={reconnectAll}>
              Reconnect All
            </Button>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Connections Table */}
      <Card
        title={
          <Space>
            <WifiOutlined />
            <Title level={4} style={{ margin: 0 }}>
              WebSocket Connections
            </Title>
            <Badge count={connectedCount} />
          </Space>
        }
        extra={
          <Space>
            <Text>Auto Reconnect:</Text>
            <Switch
              checked={autoReconnect}
              onChange={setAutoReconnect}
              size="small"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={reconnectAll}
              size="small"
            >
              Reconnect All
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={connectionArray}
          rowKey="id"
          size="small"
        />
      </Card>

      {/* Recent Messages */}
      {detailed && messageLog.length > 0 && (
        <Card
          title="Recent Messages"
          style={{ marginTop: 24 }}
          extra={
            <Button
              size="small"
              onClick={() => setMessageLog([])}
            >
              Clear Log
            </Button>
          }
        >
          <Timeline
            items={messageLog.slice(-10).reverse().map(msg => ({
              color: msg.type === 'sent' ? 'blue' : 'green',
              children: (
                <Space orientation="vertical" size={0}>
                  <Space>
                    <Tag color={msg.type === 'sent' ? 'blue' : 'green'}>
                      {msg.type.toUpperCase()}
                    </Tag>
                    <Text strong>{msg.connectionId}</Text>
                    <Text type="secondary">
                      {formatDate(msg.timestamp.toISOString(), { timeStyle: 'short' })}
                    </Text>
                  </Space>
                  <code style={{ fontSize: '12px', backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>
                    {JSON.stringify(msg.data, null, 2).substring(0, 100)}
                    {JSON.stringify(msg.data).length > 100 && '...'}
                  </code>
                </Space>
              ),
            }))}
          />
        </Card>
      )}

      {/* Connection Details Modal */}
      <Modal
        title="Connection Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={800}
      >
        {selectedConnection && connections[selectedConnection] && (
          <div>
            <Space orientation="vertical" size="large" style={{ width: '100%' }}>
              <Alert
                title="Connection Information"
                description={`Detailed metrics and status for ${selectedConnection} WebSocket connection.`}
                type="info"
                showIcon
              />

              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Connection Status" size="small">
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>Status:</Text>
                        <Tag color={getStatusColor(connections[selectedConnection].state.status)}>
                          {connections[selectedConnection].state.status.toUpperCase()}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>Messages:</Text>
                        <Text>{formatNumber(connections[selectedConnection].state.messageCount)}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>Errors:</Text>
                        <Text>{connections[selectedConnection].state.errorCount}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>Latency:</Text>
                        <Text>{connections[selectedConnection].state.latency || '-'}ms</Text>
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Subscriptions" size="small">
                    <Space wrap>
                      {Array.from(connections[selectedConnection].subscriptions).map(sub => (
                        <Tag key={sub}>{sub}</Tag>
                      ))}
                      {connections[selectedConnection].subscriptions.size === 0 && (
                        <Text type="secondary">No active subscriptions</Text>
                      )}
                    </Space>
                  </Card>
                </Col>
              </Row>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WebSocketMonitor;
